// ABOUTME: Admin interface for managing knowledge base articles
// ABOUTME: Provides CRUD operations with list view, filters, and dialogs

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  CircularProgress,
  Typography,
  Alert,
  SelectChangeEvent,
  Autocomplete,
} from '@mui/material';
import { Add, Edit, Delete, ContentCopy } from '@mui/icons-material';
import { getKBArticles, getKBArticle, createKBArticle, updateKBArticle, deleteKBArticle, getKBCategories, getKBTags } from '../../../services/api';
import { KBArticleListDto, CreateKBArticleDto, UpdateKBArticleDto, KBCategoryDto, KBTagDto } from '../../../types/knowledgeBase';
import RichTextEditor from './RichTextEditor';

const ArticleManager: React.FC = () => {
  const [articles, setArticles] = useState<KBArticleListDto[]>([]);
  const [categories, setCategories] = useState<KBCategoryDto[]>([]);
  const [tags, setTags] = useState<KBTagDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Helper function to flatten hierarchical categories for dropdown display
  const flattenCategories = (cats: KBCategoryDto[], level = 0): Array<{ id: number; name: string; level: number }> => {
    let result: Array<{ id: number; name: string; level: number }> = [];
    cats.forEach(cat => {
      result.push({ id: cat.id, name: cat.name, level });
      if (cat.childCategories && cat.childCategories.length > 0) {
        result = result.concat(flattenCategories(cat.childCategories, level + 1));
      }
    });
    return result;
  };

  // Helper function to detect if form has changed from original
  const hasFormChanged = (): boolean => {
    if (!editingArticle || !originalFormData) return true; // For new articles, always enabled if required fields filled
    return JSON.stringify(formData) !== JSON.stringify(originalFormData);
  };

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState<KBArticleListDto | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState<KBArticleListDto | null>(null);
  const [unsavedChangesDialogOpen, setUnsavedChangesDialogOpen] = useState(false);

  // Form state
  const [formData, setFormData] = useState<CreateKBArticleDto>({
    title: '',
    content: '',
    description: null,
    thumbnailUrl: null,
    categoryId: null,
    publishStatus: 'Draft',
    accessControl: 'Authenticated',
    isUserFacingVisible: false,
    tagIds: [],
  });
  const [originalFormData, setOriginalFormData] = useState<CreateKBArticleDto | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setError(null);
      const [articlesData, categoriesData, tagsData] = await Promise.all([
        getKBArticles(),
        getKBCategories(),
        getKBTags(),
      ]);
      setArticles(articlesData);
      setCategories(categoriesData);
      setTags(tagsData);
    } catch (err) {
      console.error('Failed to load articles:', err);
      setError('Failed to load articles');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateDialog = () => {
    setEditingArticle(null);
    const newFormData = {
      title: '',
      content: '',
      description: null,
      thumbnailUrl: null,
      categoryId: null,
      publishStatus: 'Draft',
      accessControl: 'Authenticated',
      isUserFacingVisible: false,
      tagIds: [],
    };
    setFormData(newFormData);
    setOriginalFormData(null);
    setDialogOpen(true);
  };

  const handleOpenEditDialog = async (article: KBArticleListDto) => {
    try {
      setEditingArticle(article);
      // Fetch full article to get content and all fields
      const fullArticle = await getKBArticle(article.id);
      const editFormData = {
        title: fullArticle.title,
        content: fullArticle.content,
        description: fullArticle.description,
        thumbnailUrl: fullArticle.thumbnailUrl,
        categoryId: fullArticle.categoryId,
        publishStatus: fullArticle.publishStatus,
        accessControl: fullArticle.accessControl,
        isUserFacingVisible: fullArticle.isUserFacingVisible,
        tagIds: fullArticle.tags.map(t => t.id),
      };
      setFormData(editFormData);
      setOriginalFormData(editFormData);
      setDialogOpen(true);
    } catch (err) {
      console.error('Failed to load article for editing:', err);
      setError('Failed to load article details');
    }
  };

  const handleDuplicateArticle = async (article: KBArticleListDto) => {
    try {
      // Fetch full article to get all content
      const fullArticle = await getKBArticle(article.id);

      // Create duplicate data with "Copy of " prefix and Draft status
      const duplicateFormData = {
        title: `Copy of ${fullArticle.title}`,
        content: fullArticle.content,
        description: fullArticle.description,
        thumbnailUrl: fullArticle.thumbnailUrl,
        categoryId: fullArticle.categoryId,
        publishStatus: 'Draft' as const,
        accessControl: fullArticle.accessControl,
        isUserFacingVisible: fullArticle.isUserFacingVisible,
        tagIds: fullArticle.tags.map(t => t.id),
      };

      // Open dialog as new article (not editing)
      setEditingArticle(null);
      setFormData(duplicateFormData);
      setOriginalFormData(null);
      setDialogOpen(true);
    } catch (err) {
      console.error('Failed to duplicate article:', err);
      setError('Failed to duplicate article');
    }
  };

  const handleCloseDialog = () => {
    if (hasFormChanged()) {
      setUnsavedChangesDialogOpen(true);
    } else {
      setDialogOpen(false);
      setEditingArticle(null);
    }
  };

  const handleConfirmClose = () => {
    setUnsavedChangesDialogOpen(false);
    setDialogOpen(false);
    setEditingArticle(null);
  };

  const handleCancelClose = () => {
    setUnsavedChangesDialogOpen(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingArticle) {
        const updateDto: UpdateKBArticleDto = {
          title: formData.title,
          content: formData.content,
          description: formData.description,
          thumbnailUrl: formData.thumbnailUrl,
          categoryId: formData.categoryId,
          publishStatus: formData.publishStatus,
          accessControl: formData.accessControl,
          isUserFacingVisible: formData.isUserFacingVisible,
          tagIds: formData.tagIds,
        };
        await updateKBArticle(editingArticle.id, updateDto);
      } else {
        await createKBArticle(formData);
      }
      // Close dialog directly after successful save
      setDialogOpen(false);
      setEditingArticle(null);
      loadData();
    } catch (err) {
      console.error('Failed to save article:', err);
      setError('Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDeleteDialog = (article: KBArticleListDto) => {
    setArticleToDelete(article);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setArticleToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!articleToDelete) return;

    try {
      await deleteKBArticle(articleToDelete.id);
      handleCloseDeleteDialog();
      loadData();
    } catch (err) {
      console.error('Failed to delete article:', err);
      setError('Failed to delete article');
    }
  };

  const handleFormChange = (field: keyof CreateKBArticleDto, value: CreateKBArticleDto[keyof CreateKBArticleDto]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSelectChange = (event: SelectChangeEvent) => {
    const { name, value } = event.target;
    handleFormChange(name as keyof CreateKBArticleDto, value);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Articles</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpenCreateDialog}
        >
          Create Article
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Articles Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Preview</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Views</TableCell>
              <TableCell>Tags</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {articles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No articles found. Create your first article to get started.
                </TableCell>
              </TableRow>
            ) : (
              articles.map((article) => (
                <TableRow key={article.id}>
                  <TableCell>{article.title}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: 300 }}>
                      {article.thumbnailUrl && (
                        <Box
                          component="img"
                          src={article.thumbnailUrl}
                          alt=""
                          sx={{
                            width: 50,
                            height: 50,
                            objectFit: 'cover',
                            borderRadius: 1,
                            flexShrink: 0
                          }}
                        />
                      )}
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {article.description || 'No description'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{article.categoryName || 'Uncategorized'}</TableCell>
                  <TableCell>
                    <Chip
                      label={article.publishStatus}
                      color={article.publishStatus === 'Published' ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{article.viewCount}</TableCell>
                  <TableCell>
                    {article.tagNames.map((tag, idx) => (
                      <Chip key={idx} label={tag} size="small" sx={{ mr: 0.5 }} />
                    ))}
                  </TableCell>
                  <TableCell>{new Date(article.updatedAt).toLocaleDateString()}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEditDialog(article)}
                      title="Edit article"
                      aria-label="Edit article"
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDuplicateArticle(article)}
                      title="Duplicate article"
                      aria-label="Duplicate article"
                    >
                      <ContentCopy />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDeleteDialog(article)}
                      title="Delete article"
                      aria-label="Delete article"
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingArticle ? 'Edit Article' : 'Create Article'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Title"
              value={formData.title}
              onChange={(e) => handleFormChange('title', e.target.value)}
              required
              fullWidth
            />

            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
                Content *
              </Typography>
              <RichTextEditor
                value={formData.content}
                onChange={(html) => handleFormChange('content', html)}
                placeholder="Write your article content here..."
              />
            </Box>

            <TextField
              label="Description"
              value={formData.description || ''}
              onChange={(e) => handleFormChange('description', e.target.value || null)}
              multiline
              rows={2}
              fullWidth
            />

            <TextField
              label="Thumbnail URL"
              value={formData.thumbnailUrl || ''}
              onChange={(e) => handleFormChange('thumbnailUrl', e.target.value || null)}
              fullWidth
            />

            <FormControl fullWidth required>
              <InputLabel required>Category</InputLabel>
              <Select
                name="categoryId"
                value={formData.categoryId?.toString() || ''}
                onChange={(e) => handleFormChange('categoryId', e.target.value ? parseInt(e.target.value) : null)}
              >
                {flattenCategories(categories).map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {'\u00A0\u00A0\u00A0'.repeat(cat.level)}{cat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Publish Status</InputLabel>
              <Select
                name="publishStatus"
                value={formData.publishStatus}
                onChange={handleSelectChange}
                required
              >
                <MenuItem value="Draft">Draft</MenuItem>
                <MenuItem value="Published">Published</MenuItem>
                <MenuItem value="Archived">Archived</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Access Control</InputLabel>
              <Select
                name="accessControl"
                value={formData.accessControl}
                onChange={handleSelectChange}
                required
              >
                <MenuItem value="Public">Public</MenuItem>
                <MenuItem value="Authenticated">Authenticated</MenuItem>
                <MenuItem value="Family">Family</MenuItem>
              </Select>
            </FormControl>

            <Autocomplete
              multiple
              options={tags}
              getOptionLabel={(option) => option.name}
              value={tags.filter(tag => formData.tagIds.includes(tag.id))}
              onChange={(_event, newValue) => {
                handleFormChange('tagIds', newValue.map(tag => tag.id));
              }}
              renderInput={(params) => (
                <TextField {...params} label="Tags" placeholder="Select tags" />
              )}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isUserFacingVisible}
                  onChange={(e) => handleFormChange('isUserFacingVisible', e.target.checked)}
                />
              }
              label="Visible to Users"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !formData.title || !formData.content || !formData.categoryId || (!!editingArticle && !hasFormChanged())}
          >
            {saving ? <CircularProgress size={20} /> : (editingArticle ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete Article</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete &quot;{articleToDelete?.title}&quot;? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unsaved Changes Confirmation Dialog */}
      <Dialog open={unsavedChangesDialogOpen} onClose={handleCancelClose}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography>
            You have unsaved changes. Are you sure you want to close without saving?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClose}>Continue Editing</Button>
          <Button onClick={handleConfirmClose} color="error" variant="contained">
            Discard Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ArticleManager;
